import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const frontOfficeAgents = [
  { name: 'Amy', role: 'Receptionist', description: 'Handles inbound calls 24/7 with zero patient wait times. Schedules directly into Asha PMS after go-live.', image: '/assets/amy.png', link: '/agents/receptionist', skills: ['Inbound Calls', 'Scheduling', 'Q&A'] },
  { name: 'Amanda', role: 'Insurance Verification', description: 'Automatically verifies patient insurance benefits and eligibility hours before visits.', image: '/assets/amanda.png', link: '/agents/insurance-verification', skills: ['Verification', 'Benefits', 'Eligibility'] },
  { name: 'Leena', role: 'Schedule Optimizer', description: 'Prevents no-shows and fills cancellations with new patients to maximize production.', image: '/assets/leena.png', link: '/agents/schedule-optimizer', skills: ['Schedule Filling', 'Confirmation', 'Recall'] },
  { name: 'Stephanie', role: 'Hygiene Recall', description: 'Proactively calls unscheduled patients to book their hygiene appointments.', image: '/assets/stephanie.png', link: '/agents/hygiene-recall', skills: ['Outbound Calls', 'Reactivation', 'Booking'] },
  { name: 'Kim', role: 'Case Acceptance', description: 'Helps patients understand treatment plans and move forward with care.', image: '/assets/kim.png', skills: ['Treatment Pres.', 'Education', 'Sales'] },
  { name: 'Morgan', role: 'Patient Intake', description: 'Ensures patients complete intake forms and provide insurance info before they arrive.', image: '/assets/morgan.png', skills: ['Forms', 'Insurance', 'Intake'] },
];

const backOfficeAgents = [
  { name: 'Alisha', role: 'EOB Posting', description: 'Posts explanation of benefits and insurance payments automatically.', image: '/assets/alisha.png', skills: ['Posting', 'Reconciliation', 'Data Entry'] },
  { name: 'Laura', role: 'Insurance Calls', description: 'Handles insurance-related phone calls and inquiries efficiently.', image: 'https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png', skills: ['Inbound Calls', 'Inquiries', 'Support'] },
  { name: 'Amanda', role: 'Insurance Verification', description: 'Automatically verifies patient insurance benefits and eligibility hours before visits.', image: '/assets/amanda.png', link: '/agents/insurance-verification', skills: ['Verification', 'Benefits', 'Eligibility'] },
  { name: 'Ariel', role: 'Claim Submission', description: 'Generates and submits clean insurance claims to payers instantly.', image: '/assets/ariel.png', skills: ['Claims', 'Submission', 'Coding'] },
  { name: 'Sarah', role: 'Denial Management', description: 'Handles denied claims, appeals, and resubmissions automatically.', image: '/assets/sarah.png', skills: ['Appeals', 'Resubmission', 'Negotiation'] },
  { name: 'Kianna', role: 'Patient Billing', description: 'Manages patient statements and answers billing questions via text/phone.', image: '/assets/kianna.png', skills: ['Statements', 'Payments', 'Inquiries'] },
];

const AgentsSection = () => {
  const [activeTab, setActiveTab] = useState('front-office');
  const displayAgents = activeTab === 'front-office' ? frontOfficeAgents : backOfficeAgents;

  return (
    <section id="agents" className="py-24 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Digital Workforce</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-[1.1] tracking-tight">
              The only platform with <span className="text-blue-600">14+ dental AI agents</span> for every use case
            </h2>
            <p className="text-lg text-gray-500 leading-relaxed">
              We're the only company in the world that has a full team of AI agents that work collaboratively to automate your entire front and back office.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="bg-gray-100 p-1 rounded-full inline-flex flex-shrink-0 relative">
            <motion.div
              className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm z-0"
              initial={false}
              animate={{
                left: activeTab === 'front-office' ? '4px' : '50%',
                width: 'calc(50% - 6px)',
                x: activeTab === 'front-office' ? 0 : 2
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
            {['front-office', 'back-office'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 relative z-10 ${
                  activeTab === tab ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {tab === 'front-office' ? 'Front Office' : 'Back Office'}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {displayAgents.map((agent) => {
              const CardContent = () => (
                <>
                  <div className="p-6 pb-0 flex items-start justify-between">
                    <div className="relative">
                      <div className="w-28 h-28 rounded-2xl overflow-hidden ring-1 ring-gray-100">
                        <img src={agent.image} alt={agent.name} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-green-500 border-[3px] border-white w-6 h-6 rounded-full" />
                    </div>
                    {agent.link && (
                      <div className="w-8 h-8 rounded-full bg-gray-50 group-hover:bg-blue-600 flex items-center justify-center transition-all duration-300">
                        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-gray-900 mb-0.5">{agent.name}</h3>
                      <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider">{agent.role}</p>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{agent.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {agent.skills?.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-500 text-[11px] font-semibold border border-gray-100 uppercase tracking-wider">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              );

              return (
                <motion.div key={agent.name + agent.role} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.25 }}>
                  {agent.link ? (
                    <Link to={agent.link} className="group relative bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
                      <CardContent />
                    </Link>
                  ) : (
                    <div className="group relative bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
                      <CardContent />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default AgentsSection;
